import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild,
  NgZone,
  SimpleChanges
} from '@angular/core'
import {
  combineLatest,
  merge,
  of,
  ReplaySubject,
  Subject,
  fromEvent,
  SchedulerLike,
  BehaviorSubject,
  timer
} from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  flatMap,
  map,
  mergeMap,
  share,
  shareReplay,
  skipWhile,
  switchMap,
  take,
  takeUntil,
  tap,
  throttleTime,
  withLatestFrom
} from 'rxjs/operators'

import { IVolumeControls } from '../../video-player/models/volume-controls.model'
import { VideoAdsService } from '../services/video-ads.service'
import { IVideo } from '../../models/video.model'
import { IdGeneratorService } from '../../../singleton-services/id-generator.service'
import { YOUBORA_IMA_ADAPTER } from '../../analytics/directives/video-analytics.directive'
import { RXJS_SCHEDULER } from 'src/app/app.config'
import * as preRollFunctions from '../../functions/preroll-ads'
import { DOCUMENT } from '@angular/common'
import { SegmentService } from '../../analytics/services/segment.service'
import { SegmentTrackVodPrerollAds } from '../../interfaces/segment-track-vod-preroll-ads.interface'
import { SegmentTrackLiveAds } from '../../interfaces/segment-track-live-ads.interface'
import { PubmaticService } from '../advertising-service/pubmatic.service'
import { isValidAdEvent, notNullOrUndefined } from '../../functions/type-guards'
import { normalizeVolume } from '../ad-volume.utility'
import { LiveEventStatus } from 'src/app/events/events.enum'

@Component({
  selector: 'flo-ad-video-preroll',
  template: `
    <div class="ad-video-preroll d-none" #adVideoPrerollElement>
      <div class="video-ad-container" [id]="adContainerId$ | async" #adContainerElement></div>
    </div>
  `,
  styles: [
    `
      .ad-video-preroll {
        height: 100%;
        width: 100%;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
      }
    `
  ]
})
export class AdVideoPrerollComponent implements OnChanges, OnDestroy, AfterViewInit {
  @ViewChild('adVideoPrerollElement')
  set adVideoPrerollElement(elementRef: ElementRef<HTMLDivElement>) {
    if (elementRef && elementRef.nativeElement) this._adVideoPrerollElement.next(elementRef.nativeElement)
  }

  @ViewChild('adContainerElement')
  set adContainerElement(elementRef: ElementRef<HTMLDivElement>) {
    if (elementRef && elementRef.nativeElement) this._adContainerElement.next(elementRef.nativeElement)
  }

  @Input()
  set video(video: IVideo) {
    this._video.next(video)
  }

  @Input()
  set analyticsPlugin(analyticsPlugin: any) {
    this._analyticsPlugin.next(analyticsPlugin)
  }

  @Input()
  set enablePreroll(enablePreroll: boolean) {
    this._enablePreroll.next(!!enablePreroll)
  }

  @Input()
  set contentVideoElement(contentVideoElement: HTMLVideoElement) {
    this._contentVideoElement.next(contentVideoElement)
  }

  constructor(
    private readonly zone: NgZone,
    private readonly renderer2: Renderer2,
    private readonly videoAdsService: VideoAdsService,
    private readonly idGeneratorService: IdGeneratorService,
    private readonly segmentService: SegmentService,
    private readonly pubmaticService: PubmaticService,
    @Inject(YOUBORA_IMA_ADAPTER) private readonly adAnalyticsAdapter: any,
    @Inject(RXJS_SCHEDULER) private readonly scheduler: SchedulerLike,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}
  private readonly ngAfterViewInit$ = new Subject<void>()
  private readonly ngOnDestroy$ = new Subject<void>()
  private readonly ngOnChanges$ = new Subject<SimpleChanges>()
  private readonly _volumeControls = new ReplaySubject<IVolumeControls>(1)
  private readonly _contentVideoElement = new ReplaySubject<HTMLVideoElement>(1)
  private readonly _video = new ReplaySubject<IVideo>(1)
  private readonly _adEvent = new Subject<string>()
  private readonly _enablePreroll = new BehaviorSubject<boolean>(false)
  public readonly _adVideoPrerollElement = new ReplaySubject<HTMLDivElement>(1)
  private readonly _adContainerElement = new ReplaySubject<HTMLDivElement>(1)
  private readonly _analyticsPlugin = new ReplaySubject<any>(1)
  private readonly _adStarted = new BehaviorSubject<boolean>(false)

  @Input()
  public readonly volumeControls?: IVolumeControls
  private readonly volumeControls$ = this.ngOnChanges$.pipe(
    preRollFunctions.mapChanges<IVolumeControls>('volumeControls')
  )

  @Output()
  public readonly adEvent$ = this._adEvent

  private readonly adVideoPrerollElement$ = this._adVideoPrerollElement.pipe(
    shareReplay(1),
    distinctUntilChanged(),
    takeUntil(this.ngOnDestroy$)
  )

  private readonly adContainerElement$ = this._adContainerElement.pipe(
    shareReplay(1),
    distinctUntilChanged(),
    takeUntil(this.ngOnDestroy$)
  )

  private readonly analyticsPlugin$ = this._analyticsPlugin

  private readonly openwrapScript$ = this.ngAfterViewInit$.pipe(
    map(() => this.pubmaticService.pwt),
    filter(notNullOrUndefined),
    take(1),
    shareReplay(1)
  )

  private readonly video$ = this._video.pipe(filter<IVideo>(Boolean), shareReplay(1), takeUntil(this.ngOnDestroy$))

  private readonly isLive$ = this.video$.pipe(
    map(video => video?.eventStatus === LiveEventStatus.Live),
    distinctUntilChanged()
  )

  private readonly enablePreroll$ = combineLatest([
    this._enablePreroll,
    this.videoAdsService.canLoadPrerollAds$,
    this.isLive$
  ]).pipe(
    map(([enablePreroll, canLoadPrerollAds, isLive]) => {
      return !isLive || (enablePreroll && canLoadPrerollAds)
    }),
    distinctUntilChanged()
  )

  private readonly adStarted$ = this._adStarted

  private readonly contentVideoElement$ = this._contentVideoElement.pipe(
    filter<HTMLVideoElement>(Boolean),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  private readonly owAdProps$ = combineLatest([this.contentVideoElement$, this.video$]).pipe(
    switchMap(([_, video]) => {
      return this.videoAdsService.getPrerollAdTagUrl$(video)
    }),
    distinctUntilChanged(),
    shareReplay(1),
    take(1),
    takeUntil(this.ngOnDestroy$)
  )

  private readonly owBids$ = this.owAdProps$.pipe(
    withLatestFrom(this.contentVideoElement$),
    map(([{ iu, adParams }, video]) => this.pubmaticService.formatOwBidConfig(video, iu)),
    shareReplay(1)
  )

  private adTagUrl$ = combineLatest([this.owBids$, this.openwrapScript$]).pipe(
    withLatestFrom(this.owAdProps$),
    mergeMap(([[owBids, owScript], owParams]) =>
      this.pubmaticService.generateDFPUrl$(owBids, owScript, owParams.adParams)
    ),
    distinctUntilChanged(),
    shareReplay(1)
  )

  public readonly adContainerId$ = of(`ad-container-${this.idGeneratorService.getUniqueId()}`)

  private readonly adContainer$ = combineLatest([this.adContainerElement$, this.contentVideoElement$]).pipe(
    preRollFunctions.flatMapAdDisplayContainer(this.videoAdsService),
    take(1),
    shareReplay(1)
  )

  private readonly adsLoader$ = this.adContainer$.pipe(
    switchMap(adContainer => this.videoAdsService.getAdsLoader$(adContainer)),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  public readonly registerContentCompleteListener = combineLatest([this.adsLoader$, this.contentVideoElement$])
    .pipe(
      flatMap(([adsLoader, contentVideoElement]: readonly [google.ima.AdsLoader, HTMLVideoElement]) =>
        fromEvent(contentVideoElement, 'ended').pipe(map(() => adsLoader))
      ),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(e => {
      e.contentComplete()
    })

  private readonly adsRequest$ = this.adTagUrl$.pipe(
    withLatestFrom(this.contentVideoElement$),
    preRollFunctions.flatMapGetAdsRequest(this.videoAdsService),
    preRollFunctions.distinctUntilAdTagUrlChanged(),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  private readonly adsManager$ = combineLatest([this.adsLoader$, this.videoAdsService.videoAdsLibrary$]).pipe(
    switchMap(([adsLoader, videoAdsLibrary]) =>
      preRollFunctions.fromAdsEvent(adsLoader, videoAdsLibrary.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED)
    ),
    withLatestFrom(this.contentVideoElement$),
    preRollFunctions.mapGetAdsManager(),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  private readonly adsLoaderError$ = this.adsLoader$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    preRollFunctions.flatMapAdErrors(),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  private readonly adsManagerError$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    preRollFunctions.flatMapAdErrors(),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  private readonly pauseContentRequest$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    flatMap(([adsManager, videoAdsLibrary]) =>
      preRollFunctions.fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.CONTENT_PAUSE_REQUESTED)
    ),
    takeUntil(this.ngOnDestroy$)
  )

  private readonly resumeContentRequest$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    flatMap(([adsManager, videoAdsLibrary]) =>
      preRollFunctions.fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.CONTENT_RESUME_REQUESTED)
    ),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  private readonly videoAdClicked$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    flatMap(([adsManager, videoAdsLibrary]) =>
      preRollFunctions.fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.CLICK)
    ),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  private readonly videoAdPaused$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    flatMap(([adsManager, videoAdsLibrary]) => {
      return preRollFunctions.fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.PAUSED)
    }),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  private readonly videoAdResumed$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    flatMap(([adsManager, videoAdsLibrary]) => {
      return preRollFunctions.fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.RESUMED)
    }),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  private readonly videoAdFirstQuartile$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    flatMap(([adsManager, videoAdsLibrary]) => {
      return preRollFunctions.fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.FIRST_QUARTILE)
    }),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  private readonly videoAdMidPoint$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    flatMap(([adsManager, videoAdsLibrary]) => {
      return preRollFunctions.fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.MIDPOINT)
    }),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  private readonly videoAdThirdQuartile$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    flatMap(([adsManager, videoAdsLibrary]) => {
      return preRollFunctions.fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.THIRD_QUARTILE)
    }),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  private readonly adImpression$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    preRollFunctions.flatMapAdImpression(),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  private readonly adVolumeChanged$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    preRollFunctions.flatMapVolumeChanged(),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  private readonly isAdPaused$ = merge(
    this.videoAdPaused$.pipe(map(() => true)),
    this.videoAdResumed$.pipe(map(() => false))
  )

  private readonly videoAdsEventsList$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    map(([_, videoAdsLibrary]) => {
      return preRollFunctions.mapAdEventsToSegment(videoAdsLibrary)
    }),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  private readonly adTimeout$ = timer(5000).pipe(share())

  public readonly logAdEvents = merge(
    this.pauseContentRequest$,
    this.resumeContentRequest$,
    this.videoAdClicked$,
    this.videoAdPaused$,
    this.videoAdResumed$,
    this.videoAdFirstQuartile$,
    this.videoAdMidPoint$,
    this.videoAdThirdQuartile$,
    this.adsLoaderError$,
    this.adsManagerError$,
    this.adVolumeChanged$
  )
    .pipe(
      filter(isValidAdEvent),
      withLatestFrom(this.videoAdsEventsList$, this.video$, this.isLive$),
      map(([adEvent, segmentEventsMap, video, isLive]: any) => [
        adEvent,
        segmentEventsMap[adEvent.type],
        video,
        isLive
      ]),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(([adEvent, adEventName, video, isLive]) => {
      const ad = adEvent.getAd()

      const commonProps = {
        name: 'Pre Roll',
        ad_type: video?.type || video?.eventName,
        creative_ad_id: ad?.getCreativeAdId?.(),
        ad_id: ad?.getAdId?.(),
        action: adEvent.type === 'click' ? 'Click' : undefined
      }

      if (isLive) {
        const props: SegmentTrackLiveAds = {
          ...commonProps,
          creative_id: ad?.getCreativeId?.()
        }
        this.segmentService.trackLiveAd(props, adEventName)
      } else {
        const context: SegmentTrackVodPrerollAds = {
          ...commonProps,
          node_id: video?.id || video?.eventId,
          node_type: video?.type
        }
        this.segmentService.trackVODPrerollAd(context, adEventName)
      }
    })

  public readonly logAdImpression = this.adImpression$.subscribe(() => {
    this._adEvent.next('adImpression')
  })

  public readonly logAdClicked = this.videoAdClicked$.subscribe(() => this._adEvent.next('adClick'))

  public readonly isAdResumable = combineLatest([fromEvent(this.document, 'visibilitychange'), this.isAdPaused$])
    .pipe(withLatestFrom(this.adsManager$), takeUntil(this.ngOnDestroy$))
    .subscribe(([[document, isAdPaused], adsManager]: [[any, boolean], google.ima.AdsManager]) => {
      if (document.target.visibilityState === 'visible' && isAdPaused) adsManager.resume()
    })

  public readonly normalizeAdVolume = combineLatest([this.adsManager$, this.adVolumeChanged$])
    .pipe(takeUntil(this.ngOnDestroy$))
    .subscribe(([adsManager, _]) => {
      const currentVolume = adsManager.getVolume()
      const normalizedVolume = normalizeVolume(currentVolume, 0.6)
      adsManager.setVolume(normalizedVolume)
      this._adEvent.next('volumeChanged')
    })

  public readonly requestAd = combineLatest([this.adsRequest$, this.adsLoader$, this.enablePreroll$])
    .pipe(
      filter(([_, __, enablePreroll]) => enablePreroll),
      throttleTime(30 * 1000, this.scheduler),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(([adsRequest, adsLoader, _enablePreroll]) => {
      this.zone.runOutsideAngular(() => {
        adsLoader.requestAds(adsRequest)
        this._adEvent.next('adRequest')
      })
    })

  public readonly initializeAdsManager = combineLatest([this.adsManager$, this.contentVideoElement$])
    .pipe(takeUntil(this.ngOnDestroy$))
    .subscribe(([adsManager, contentVideoElement]) => {
      this.videoAdsService.initAdsManager(adsManager, contentVideoElement)
    })

  public readonly initializeAdAnalytics = combineLatest([this.analyticsPlugin$, this.adsManager$])
    .pipe(takeUntil(this.ngOnDestroy$))
    .subscribe(([plugin, adsManager]) => {
      this.zone.runOutsideAngular(() => {
        plugin?.setAdsAdapter(new this.adAnalyticsAdapter(adsManager))
      })
    })

  public readonly startAd = this.pauseContentRequest$
    .pipe(
      withLatestFrom(
        this.adContainerElement$,
        this.adVideoPrerollElement$,
        this.contentVideoElement$,
        this.volumeControls$,
        this.adsManager$,
        this.enablePreroll$
      ),
      filter(([_, __, ___, ____, _____, ______, enablePreroll]) => enablePreroll),
      tap(
        ([
          contentPauseRequest,
          adContainerElement,
          adVideoPrerollElement,
          contentVideoElement,
          volumeControls,
          adsManager
        ]) => {
          this.handleStartAd(adContainerElement, adVideoPrerollElement, contentVideoElement, volumeControls, adsManager)
        }
      )
    )
    .subscribe()

  private handleStartAd(
    adContainerElement: HTMLElement,
    adVideoPrerollElement: HTMLDivElement,
    contentVideoElement: HTMLVideoElement,
    volumeControls: IVolumeControls,
    adsManager: google.ima.AdsManager
  ): void {
    const renderer2 = this.renderer2
    renderer2.removeClass(adVideoPrerollElement, 'd-none')
    renderer2.setStyle(contentVideoElement, 'visibility', 'hidden')
    const currentVolume = volumeControls.isMuted ? 0 : volumeControls.volume ?? 1
    adsManager.setVolume(currentVolume)
    // TODO: Bug causes adsManager.init to receive width=0, height=0. This removes the style Google gives it
    Array.from(adContainerElement.children).forEach((element: HTMLElement) => {
      renderer2.removeAttribute(element, 'style')
      renderer2.removeClass(element, 'd-none')
    })
    const insertedElements = preRollFunctions.flatten(preRollFunctions.getDescendantElements(adContainerElement))
    insertedElements.forEach((element: HTMLElement) => {
      renderer2.removeClass(element, 'd-none')
      renderer2.addClass(element, 'ad-video-inserted-element')
    })
    renderer2.removeClass(adContainerElement, 'd-none')
    this._adEvent.next('adStarted')
    this._adStarted.next(true)
  }

  public readonly stopAd = merge(
    this.resumeContentRequest$,
    this.adsLoaderError$,
    this.adsManagerError$,
    this.adTimeout$.pipe(
      withLatestFrom(this.adStarted$),
      filter(([_, adStarted]) => !adStarted),
      map(([event]) => event)
    )
  )
    .pipe(
      withLatestFrom(
        this.adContainerElement$,
        this.contentVideoElement$,
        this.adVideoPrerollElement$,
        this.volumeControls$
      )
    )
    .subscribe(
      ([
        resumeContentRequestOrError,
        adContainerElement,
        contentVideoElement,
        adVideoPrerollElement,
        volumeControls
      ]) => {
        const renderer2 = this.renderer2
        const insertedElements = preRollFunctions.flatten(preRollFunctions.getDescendantElements(adContainerElement))
        insertedElements.forEach((element: HTMLElement) => {
          renderer2.addClass(element, 'd-none')
          renderer2.removeClass(element, 'ad-video-inserted-element')
        })
        renderer2.addClass(adVideoPrerollElement, 'd-none')
        renderer2.setStyle(contentVideoElement, 'visibility', 'visible')
        // ensure the contentVideoElement volume matches the volumeControls after the ad stops (or if an ad did not play)
        contentVideoElement.muted = !!volumeControls.isMuted
        this._adEvent.next('adComplete')
      }
    )

  public readonly setVolume = combineLatest([this.volumeControls$, this.adsManager$])
    .pipe(takeUntil(this.ngOnDestroy$))
    .subscribe(([volumeControls, adsManager]) => {
      volumeControls.isMuted ? adsManager.setVolume(0) : adsManager.setVolume(volumeControls.volume || 1)
    })

  public readonly handleAdsError = merge(this.adsLoaderError$, this.adsManagerError$).subscribe(adErrorEvent => {
    this._adEvent.next('adError')
    this.destroyAdsManager()
  })

  public readonly initializeVideoAdsService = this.ngAfterViewInit$
    .pipe(take(1))
    .subscribe(() => this.videoAdsService.init(this.renderer2))

  public ngAfterViewInit() {
    this.ngAfterViewInit$.next()
    this.ngAfterViewInit$.complete()
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
    this._volumeControls.complete()
    this._contentVideoElement.complete()
    this.ngOnChanges$.complete()
  }

  public ngOnChanges(simpleChanges: SimpleChanges) {
    this.ngOnChanges$.next(simpleChanges)
  }

  private destroyAdsManager = () => this.adsManager$.pipe(take(1)).subscribe(adsManager => adsManager.destroy())
}
